import { useEffect, useState } from 'react';
import { Button, Card, CardContent, CardHeader, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';

import { useUpdateSecurityMutation } from '@/apis/extended/authApi';
import { useGetCurrentUserQuery } from '@/apis/extended/userApi';
import { CommonFormControl } from '../Common/CommonFormControl';
import { SecurityStatusBadge } from './components/SecurityStatusBadge';

export const ProfileSecurity = () => {
  const theme = useTheme();
  const { data: user, refetch } = useGetCurrentUserQuery();
  const [updateSecurity] = useUpdateSecurityMutation();

  const [intervalId, setIntervalId] = useState<NodeJS.Timeout | null>(null);
  const isLoading = intervalId !== null;

  useEffect(() => {
    if (user?.securityEnabled && intervalId !== null) {
      clearInterval(intervalId);
      setIntervalId(null);
    }
  }, [user, intervalId]);

  useEffect(() => {
    if (intervalId === null) return;
    return () => clearInterval(intervalId);
  }, [intervalId]);

  const checkSecurity = () => {
    refetch();
  };

  const handleUpdateSecurity = () => {
    const checked = !user?.securityEnabled;
    updateSecurity(checked);
    if (checked && intervalId === null) {
      const id = setInterval(checkSecurity, 3000);
      setIntervalId(id);
    }
  };

  return (
    <Card
      sx={{
        mt: 2,
        border: `1px solid ${theme.palette.greyShades.border1}`,
        boxShadow: `0 2px 12px ${alpha(theme.palette.common.black, 0.05)}`,
      }}
    >
      <CardHeader
        title={
          <>
            Security{' '}
            <SecurityStatusBadge
              sx={{ ml: 2 }}
              enabled={user?.securityEnabled}
              isLoading={isLoading}
            />
          </>
        }
        titleTypographyProps={{
          variant: 'h6',
          sx: { color: 'text.primary', fontWeight: 600 },
        }}
        avatar={
          <i
            className="fa-light fa-shield-check"
            style={{ color: theme.palette.primary.main, fontSize: '1.35rem' }}
            aria-hidden
          />
        }
      />
      <CardContent>
        <CommonFormControl
          label="Secure my account:"
          labelWidth={200}
          inLine
          secondLabel={
            user?.securityEnabled
              ? 'Security is enabled'
              : isLoading
              ? 'Check your Inbox to verify email address'
              : ''
          }
        >
          {!user?.securityEnabled && !isLoading && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleUpdateSecurity}
              sx={{ fontWeight: 600, borderRadius: 2, px: 2.5, py: 1 }}
            >
              Enable Security
            </Button>
          )}
        </CommonFormControl>
      </CardContent>
    </Card>
  );
};
